import React, { useState, useEffect, useRef } from 'react';
import { Mail, X, Info, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';

interface EmailReportSectionProps {
  isMetricsEmail: boolean;
  setMetricsEmail: React.Dispatch<React.SetStateAction<boolean>>;
  metricsEmailAddress: string;
  setMetricsEmailAddress: (val: string) => void;
  triggerAutoSave: () => void;
}

const EmailReportSection: React.FC<EmailReportSectionProps> = ({
  isMetricsEmail,
  setMetricsEmail,
  metricsEmailAddress,
  setMetricsEmailAddress,
  triggerAutoSave
}) => {
  function toBoolean(val: unknown): boolean {
    return val === true || val === "true";
  }

  const [sendEmailSummary, setSendEmailSummary] = useState(toBoolean(isMetricsEmail));
  const [emailAddress, setEmailAddress] = useState(metricsEmailAddress);
  const [emailError, setEmailError] = useState(false);
  const [showEmailReportBlock, setShowEmailReportBlock] = useState(true);

  useEffect(() => {
    setSendEmailSummary(toBoolean(isMetricsEmail));
  }, [isMetricsEmail]);

  useEffect(() => {
    setEmailAddress(metricsEmailAddress);
  }, [metricsEmailAddress]);

  const isFirstRender = useRef(true);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialValue = useRef(emailAddress);
  const hasInteracted = useRef(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!hasInteracted.current) return;
    if (emailAddress === isInitialValue.current) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      const isValid = validateEmailList(emailAddress);
      setEmailError(!isValid);

      if (isValid && sendEmailSummary) {
        triggerAutoSave();
      }
    }, 500);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [emailAddress, sendEmailSummary, triggerAutoSave]);

  const validateEmailList = (input: string): boolean => {
    const emails = input.split(';').map(email => email.trim()).filter(email => email !== "");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every(email => emailRegex.test(email));
  };

  const handleToggleEmailSummary = () => {
    const newValue = !sendEmailSummary;
    setSendEmailSummary(newValue);
    setEmailError(false);
    setShowEmailReportBlock(true);
    setMetricsEmail(newValue);
    triggerAutoSave();
  };

  return (
    <div className="lg:col-span-2">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gray-300 rounded-lg p-2.5 shadow-sm">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black">Email Report</h3>
              <p className="text-gray-400 text-sm">Get your metrics summary delivered</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Toggle */}
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer"
                checked={sendEmailSummary}
                onChange={handleToggleEmailSummary}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-indigo-300 
                              dark:peer-focus:ring-indigo-800 peer-checked:after:translate-x-full 
                              peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 
                              after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full 
                              after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
            {/* Collapse/Expand Arrow */}
            <button
              onClick={() => setShowEmailReportBlock(!showEmailReportBlock)}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              {showEmailReportBlock ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Email Form Block */}
        {showEmailReportBlock && sendEmailSummary && (
          <div className="mt-4 rounded-xl overflow-hidden transition-all duration-300 ease-in-out">
            <div className="p-5 bg-white rounded-xl border border-indigo-100 shadow-sm">
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-1">
                  <span>Send to Email Address</span>
                  <div className="relative group">
                    <Info className="h-4 w-4 text-gray-400 cursor-pointer" />
                    <div className="absolute left-6 top-0 z-10 hidden group-hover:block bg-white border border-gray-300 text-gray-700 text-xs rounded-md shadow-md p-2 w-60">
                      For multiple email addresses, separate them with semicolon.
                    </div>
                  </div>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    className={`pl-10 block w-full rounded-lg border ${
                      emailError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'focus:ring-black '
                    } shadow-sm py-2.5 px-3 text-gray-900 placeholder-gray-400 focus:outline-none  transition-all`}
                    placeholder="you@example.com; second@example.com"
                    value={emailAddress}
                    onChange={(e) => {
                      hasInteracted.current = true;
                      const value = e.target.value;
                      setEmailAddress(value);
                      setMetricsEmailAddress(value);
                      setEmailError(!validateEmailList(value));
                    }}
                  />
                  {emailAddress && (
                    <button
                      onClick={() => setEmailAddress('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {emailError && (
                  <div className="mt-1 flex items-center text-sm text-red-500">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Please enter a valid email address
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailReportSection;